import {FC, useEffect, useRef, useState} from 'react'
import type React from 'react'
import {useAuth} from '../../modules/auth'
import {api, ApiError} from '../../services/api'
import {BRAND} from '../../brand'
import {toast} from '../../utils/toast'

const API_ORIGIN = (process.env.REACT_APP_API_URL || 'https://api.qwiksurv.com/api').replace('/api', '')

function friendlyError(e: unknown, fallback: string): string {
  if (e instanceof ApiError) {
    if (e.status === 400) return e.message
    if (e.status === 401) return 'Your session has expired. Please log in again.'
    if (e.status === 404) return 'Service unavailable. Please restart the backend server.'
    if (e.status >= 500) return 'Server error. Please try again later.'
  }
  if (e instanceof TypeError) return 'Unable to connect to server. Check your connection.'
  return fallback
}

type Tab = 'profile' | 'password' | 'platform'

const NAV_TABS: {key: Tab; label: string; icon: React.ReactNode}[] = [
  {
    key: 'profile',
    label: 'Profile Info',
    icon: <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'><path d='M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2'/><circle cx='12' cy='7' r='4'/></svg>,
  },
  {
    key: 'password',
    label: 'Change Password',
    icon: <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'><rect x='3' y='11' width='18' height='11' rx='2'/><path d='M7 11V7a5 5 0 0 1 10 0v4'/></svg>,
  },
  {
    key: 'platform',
    label: 'Platform',
    icon: <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'><circle cx='12' cy='12' r='10'/><path d='M2 12h20'/><path d='M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z'/></svg>,
  },
]

const AccountSettingsPage: FC = () => {
  const {currentUser, setCurrentUser} = useAuth()

  const photoUrl = currentUser?.photo
    ? (currentUser.photo.startsWith('http') || currentUser.photo.startsWith('data:') ? currentUser.photo : `${API_ORIGIN}${currentUser.photo}`)
    : null

  const initials = ((currentUser?.first_name?.[0] ?? 'A') + (currentUser?.last_name?.[0] ?? '')).toUpperCase()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [photoUploading, setPhotoUploading] = useState(false)
  const [photoMsg, setPhotoMsg]             = useState<{type: 'success' | 'error'; text: string} | null>(null)

  const handlePhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setPhotoUploading(true); setPhotoMsg(null)
    try {
      const updated = await api.uploadPhoto(file)
      const stored = localStorage.getItem('admin_user')
      if (stored) {
        const parsed = JSON.parse(stored)
        parsed.photo = updated.photo
        localStorage.setItem('admin_user', JSON.stringify(parsed))
        setCurrentUser?.(parsed)
      }
      setPhotoMsg({type: 'success', text: 'Photo updated successfully.'})
      toast('Photo updated')
    } catch (err) {
      setPhotoMsg({type: 'error', text: friendlyError(err, 'Failed to upload photo.')})
      toast((err as any)?.message ?? 'Photo update failed', 'error')
    } finally {
      setPhotoUploading(false)
      e.target.value = ''
    }
  }

  const [activeTab, setActiveTab] = useState<Tab>('profile')

  const [firstName, setFirstName]         = useState(currentUser?.first_name ?? '')
  const [lastName, setLastName]           = useState(currentUser?.last_name ?? '')
  const [email]                           = useState(currentUser?.email ?? '')
  const [phone, setPhone]                 = useState(currentUser?.phone ?? '')
  const [profileSaving, setProfileSaving] = useState(false)
  const [profileMsg, setProfileMsg]       = useState<{type: 'success' | 'error'; text: string} | null>(null)

  const [currentPw, setCurrentPw] = useState('')
  const [newPw, setNewPw]         = useState('')
  const [confirmPw, setConfirmPw] = useState('')
  const [pwSaving, setPwSaving]   = useState(false)
  const [pwMsg, setPwMsg]         = useState<{type: 'success' | 'error'; text: string} | null>(null)

  // Platform tax config (on/off + rate %) — applies to tickets + service bookings.
  const [taxEnabled, setTaxEnabled] = useState(false)
  const [taxRate, setTaxRate]       = useState('0')
  const [taxSaving, setTaxSaving]   = useState(false)
  const [taxMsg, setTaxMsg]         = useState<{type: 'success' | 'error'; text: string} | null>(null)

  useEffect(() => {
    api.getTaxSettings()
      .then(r => { setTaxEnabled(r.enabled); setTaxRate(String(r.ratePercent)) })
      .catch(() => {})
  }, [])

  const handleTaxSave = async (e: React.FormEvent) => {
    e.preventDefault()
    setTaxMsg(null)
    const rate = parseFloat(taxRate)
    if (!isFinite(rate) || rate < 0 || rate > 100) {
      setTaxMsg({type: 'error', text: 'Tax rate must be between 0 and 100.'})
      return
    }
    setTaxSaving(true)
    try {
      const r = await api.updateTaxSettings(taxEnabled, rate)
      setTaxEnabled(r.enabled); setTaxRate(String(r.ratePercent))
      setTaxMsg({type: 'success', text: 'Tax settings saved.'})
      toast('Tax settings saved')
    } catch (err) {
      setTaxMsg({type: 'error', text: friendlyError(err, 'Failed to save tax settings.')})
      toast((err as any)?.message ?? 'Save tax settings failed', 'error')
    } finally {
      setTaxSaving(false)
    }
  }

  // Platform payout commission (%) — deducted from organizer + provider payouts.
  const [commissionRate, setCommissionRate] = useState('15')
  const [commissionSaving, setCommissionSaving] = useState(false)
  const [commissionMsg, setCommissionMsg] = useState<{type: 'success' | 'error'; text: string} | null>(null)

  useEffect(() => {
    api.getCommissionSettings()
      .then(r => setCommissionRate(String(r.ratePercent)))
      .catch(() => {})
  }, [])

  const handleCommissionSave = async (e: React.FormEvent) => {
    e.preventDefault()
    setCommissionMsg(null)
    const rate = parseFloat(commissionRate)
    if (!isFinite(rate) || rate < 0 || rate > 100) {
      setCommissionMsg({type: 'error', text: 'Commission rate must be between 0 and 100.'})
      return
    }
    setCommissionSaving(true)
    try {
      const r = await api.updateCommissionSettings(rate)
      setCommissionRate(String(r.ratePercent))
      setCommissionMsg({type: 'success', text: 'Commission saved.'})
      toast('Commission saved')
    } catch (err) {
      setCommissionMsg({type: 'error', text: friendlyError(err, 'Failed to save commission.')})
      toast((err as any)?.message ?? 'Save commission failed', 'error')
    } finally {
      setCommissionSaving(false)
    }
  }

  const handleProfileSave = async (e: React.FormEvent) => {
    e.preventDefault()
    setProfileSaving(true); setProfileMsg(null)
    try {
      const updated = await api.updateProfile({firstName: firstName.trim(), lastName: lastName.trim(), phone: phone.trim()})
      const stored = localStorage.getItem('admin_user')
      if (stored) {
        const parsed = JSON.parse(stored)
        parsed.first_name = updated.firstName
        parsed.last_name  = updated.lastName
        parsed.phone      = updated.phone
        localStorage.setItem('admin_user', JSON.stringify(parsed))
        setCurrentUser?.(parsed)
      }
      setProfileMsg({type: 'success', text: 'Profile updated successfully.'})
      toast('Profile updated')
    } catch (err) {
      setProfileMsg({type: 'error', text: friendlyError(err, 'Failed to update profile.')})
      toast((err as any)?.message ?? 'Profile update failed', 'error')
    } finally {
      setProfileSaving(false)
    }
  }

  const handlePasswordSave = async (e: React.FormEvent) => {
    e.preventDefault()
    setPwMsg(null)
    if (newPw.length < 8) { setPwMsg({type: 'error', text: 'Password must be at least 8 characters.'}); return }
    if (newPw !== confirmPw) { setPwMsg({type: 'error', text: 'Passwords do not match.'}); return }
    setPwSaving(true)
    try {
      const res = await api.changePassword(currentPw, newPw)
      setPwMsg({type: 'success', text: res.message ?? 'Password changed successfully.'})
      setCurrentPw(''); setNewPw(''); setConfirmPw('')
      toast('Password changed')
    } catch (err) {
      setPwMsg({type: 'error', text: friendlyError(err, 'Failed to change password.')})
      toast((err as any)?.message ?? 'Password change failed', 'error')
    } finally {
      setPwSaving(false)
    }
  }

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '10px 14px', border: '1px solid #e2e8f0',
    borderRadius: 8, fontSize: 14, color: '#0f172a', background: '#f8fafc',
    outline: 'none', boxSizing: 'border-box',
  }

  const disabledInputStyle: React.CSSProperties = {
    ...inputStyle, background: '#f1f5f9', color: '#94a3b8', cursor: 'not-allowed',
  }

  const labelStyle: React.CSSProperties = {
    fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6,
  }

  const Alert: FC<{msg: {type: 'success' | 'error'; text: string}}> = ({msg}) => (
    <div style={{
      padding: '12px 16px', borderRadius: 8, fontSize: 13, fontWeight: 500, marginBottom: 20,
      background: msg.type === 'success' ? '#f0fdf4' : '#fef2f2',
      border: `1px solid ${msg.type === 'success' ? '#bbf7d0' : '#fecaca'}`,
      color: msg.type === 'success' ? '#16a34a' : '#dc2626',
      display: 'flex', alignItems: 'center', gap: 8,
    }}>
      {msg.type === 'success'
        ? <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'><polyline points='20 6 9 17 4 12'/></svg>
        : <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'><circle cx='12' cy='12' r='10'/><line x1='12' y1='8' x2='12' y2='12'/><line x1='12' y1='16' x2='12.01' y2='16'/></svg>
      }
      {msg.text}
    </div>
  )

  return (
    <div>
      {/* Header */}
      <div style={{marginBottom: 24}}>
        <h1 style={{fontSize: 24, fontWeight: 700, color: '#0f172a', margin: '0 0 4px'}}>Account Settings</h1>
        <p style={{fontSize: 14, color: '#64748b', margin: 0}}>Manage your profile and account security</p>
      </div>

      <div style={{display: 'grid', gridTemplateColumns: '260px 1fr', gap: 20, alignItems: 'start'}}>

        {/* ── Left sidebar ── */}
        <div style={{display: 'flex', flexDirection: 'column', gap: 16}}>

          {/* Avatar card */}
          <div style={{background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', padding: '28px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12}}>
            {/* Avatar */}
            <div style={{position: 'relative', cursor: photoUploading ? 'default' : 'pointer'}}
              onClick={() => !photoUploading && fileInputRef.current?.click()}>
              {photoUrl ? (
                <img src={photoUrl} alt='avatar'
                  style={{width: 88, height: 88, borderRadius: '50%', objectFit: 'cover', opacity: photoUploading ? 0.5 : 1, transition: 'opacity 0.2s', border: '3px solid #e2e8f0'}} />
              ) : (
                <div style={{
                  width: 88, height: 88, borderRadius: '50%',
                  background: BRAND.gradient,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 30, color: '#fff', fontWeight: 700,
                  opacity: photoUploading ? 0.5 : 1, transition: 'opacity 0.2s',
                  border: '3px solid #e2e8f0',
                }}>
                  {initials}
                </div>
              )}
              {/* Edit badge */}
              <div style={{
                position: 'absolute', bottom: 2, right: 2,
                width: 26, height: 26, borderRadius: '50%',
                background: BRAND.primary, border: '2px solid #fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {photoUploading
                  ? <div style={{width: 12, height: 12, border: '2px solid rgba(255,255,255,0.4)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.8s linear infinite'}} />
                  : <svg width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='#fff' strokeWidth='2.5'><path d='M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7'/><path d='M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z'/></svg>
                }
              </div>
              <input ref={fileInputRef} type='file' accept='image/*' style={{display: 'none'}} onChange={handlePhotoChange} />
            </div>

            {photoMsg && <Alert msg={photoMsg} />}

            <div style={{textAlign: 'center'}}>
              <div style={{fontSize: 16, fontWeight: 700, color: '#0f172a'}}>{currentUser?.first_name} {currentUser?.last_name}</div>
              <div style={{fontSize: 12, color: '#94a3b8', marginTop: 2}}>{currentUser?.email}</div>
              <span style={{display: 'inline-block', marginTop: 8, padding: '3px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600, background: '#fee2e2', color: '#dc2626'}}>
                Admin
              </span>
            </div>

            <div style={{width: '100%', height: 1, background: '#f1f5f9', margin: '4px 0'}} />

            {/* Tab nav */}
            <div style={{width: '100%', display: 'flex', flexDirection: 'column', gap: 4}}>
              {NAV_TABS.map(tab => (
                <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '9px 12px', borderRadius: 8, width: '100%', textAlign: 'left',
                    border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: activeTab === tab.key ? 600 : 400,
                    background: activeTab === tab.key ? BRAND.primaryLight : 'transparent',
                    color: activeTab === tab.key ? BRAND.primary : '#64748b',
                    transition: 'all 0.15s',
                  }}
                >
                  {tab.icon}
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* ── Right content ── */}
        <div>

          {/* Profile Info */}
          {activeTab === 'profile' && (
            <div style={{background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflow: 'hidden'}}>
              <div style={{padding: '20px 24px', borderBottom: '1px solid #f1f5f9'}}>
                <h3 style={{fontSize: 16, fontWeight: 700, color: '#0f172a', margin: 0}}>Profile Information</h3>
                <p style={{fontSize: 13, color: '#64748b', margin: '4px 0 0'}}>Update your personal details</p>
              </div>
              <div style={{padding: '24px'}}>
                {profileMsg && <Alert msg={profileMsg} />}
                <form onSubmit={handleProfileSave}>
                  <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16}}>
                    <div>
                      <label style={labelStyle}>First Name <span style={{color: '#ef4444'}}>*</span></label>
                      <input value={firstName} onChange={e => setFirstName(e.target.value)} required style={inputStyle} placeholder='First name' />
                    </div>
                    <div>
                      <label style={labelStyle}>Last Name <span style={{color: '#ef4444'}}>*</span></label>
                      <input value={lastName} onChange={e => setLastName(e.target.value)} required style={inputStyle} placeholder='Last name' />
                    </div>
                  </div>
                  <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16}}>
                    <div>
                      <label style={labelStyle}>Email Address</label>
                      <input type='email' value={email} disabled style={disabledInputStyle} />
                      <div style={{fontSize: 11, color: '#94a3b8', marginTop: 4}}>Email cannot be changed.</div>
                    </div>
                    <div>
                      <label style={labelStyle}>Phone Number</label>
                      <input type='tel' value={phone} onChange={e => setPhone(e.target.value)} style={inputStyle} placeholder='+1 (555) 000-0000' />
                    </div>
                  </div>
                  <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 28}}>
                    <div>
                      <label style={labelStyle}>Role</label>
                      <input value='Administrator' disabled style={disabledInputStyle} />
                    </div>
                  </div>
                  <div style={{display: 'flex', justifyContent: 'flex-end'}}>
                    <button type='submit' disabled={profileSaving}
                      style={{display: 'flex', alignItems: 'center', gap: 8, padding: '10px 24px', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, color: '#fff', background: BRAND.primary, cursor: 'pointer', opacity: profileSaving ? 0.7 : 1}}>
                      {profileSaving && <div style={{width: 14, height: 14, border: '2px solid rgba(255,255,255,0.4)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.8s linear infinite'}} />}
                      Save Changes
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Change Password */}
          {activeTab === 'password' && (
            <div style={{background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflow: 'hidden'}}>
              <div style={{padding: '20px 24px', borderBottom: '1px solid #f1f5f9'}}>
                <h3 style={{fontSize: 16, fontWeight: 700, color: '#0f172a', margin: 0}}>Change Password</h3>
                <p style={{fontSize: 13, color: '#64748b', margin: '4px 0 0'}}>Keep your account secure with a strong password</p>
              </div>
              <div style={{padding: '24px'}}>
                {pwMsg && <Alert msg={pwMsg} />}
                <form onSubmit={handlePasswordSave} style={{maxWidth: 480}}>
                  <div style={{marginBottom: 16}}>
                    <label style={labelStyle}>Current Password <span style={{color: '#ef4444'}}>*</span></label>
                    <input type='password' value={currentPw} onChange={e => setCurrentPw(e.target.value)} required style={inputStyle} placeholder='Enter current password' />
                  </div>
                  <div style={{marginBottom: 16}}>
                    <label style={labelStyle}>New Password <span style={{color: '#ef4444'}}>*</span></label>
                    <input type='password' value={newPw} onChange={e => setNewPw(e.target.value)} required style={inputStyle} placeholder='Min. 8 characters' />
                  </div>
                  <div style={{marginBottom: 20}}>
                    <label style={labelStyle}>Confirm New Password <span style={{color: '#ef4444'}}>*</span></label>
                    <input type='password' value={confirmPw} onChange={e => setConfirmPw(e.target.value)} required style={inputStyle} placeholder='Repeat new password' />
                  </div>

                  {/* Hint box */}
                  <div style={{display: 'flex', alignItems: 'flex-start', gap: 10, padding: '12px 14px', background: '#fefce8', border: '1px solid #fde68a', borderRadius: 8, marginBottom: 24}}>
                    <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='#ca8a04' strokeWidth='2' style={{flexShrink: 0, marginTop: 1}}>
                      <circle cx='12' cy='12' r='10'/><line x1='12' y1='8' x2='12' y2='12'/><line x1='12' y1='16' x2='12.01' y2='16'/>
                    </svg>
                    <span style={{fontSize: 13, color: '#92400e'}}>
                      Password must be at least <strong>8 characters</strong> long.
                    </span>
                  </div>

                  <div style={{display: 'flex', justifyContent: 'flex-end'}}>
                    <button type='submit' disabled={pwSaving}
                      style={{display: 'flex', alignItems: 'center', gap: 8, padding: '10px 24px', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, color: '#fff', background: BRAND.primary, cursor: 'pointer', opacity: pwSaving ? 0.7 : 1}}>
                      {pwSaving && <div style={{width: 14, height: 14, border: '2px solid rgba(255,255,255,0.4)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.8s linear infinite'}} />}
                      Update Password
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {activeTab === 'platform' && (
            <div style={{background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflow: 'hidden'}}>
              <div style={{padding: '20px 24px', borderBottom: '1px solid #f1f5f9'}}>
                <h3 style={{fontSize: 16, fontWeight: 700, color: '#0f172a', margin: 0}}>Tax</h3>
                <p style={{fontSize: 13, color: '#64748b', margin: '4px 0 0'}}>Charge tax on ticket purchases and service bookings</p>
              </div>
              <div style={{padding: '24px'}}>
                {taxMsg && <Alert msg={taxMsg} />}
                <form onSubmit={handleTaxSave} style={{maxWidth: 480}}>
                  {/* On/off toggle */}
                  <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', border: '1px solid #e2e8f0', borderRadius: 8, marginBottom: 16}}>
                    <div>
                      <div style={{fontSize: 14, fontWeight: 600, color: '#0f172a'}}>Enable Tax</div>
                      <div style={{fontSize: 12, color: '#64748b', marginTop: 2}}>When off, no tax is charged.</div>
                    </div>
                    <button type='button' onClick={() => setTaxEnabled(v => !v)}
                      style={{width: 46, height: 26, borderRadius: 13, border: 'none', cursor: 'pointer', padding: 3, background: taxEnabled ? BRAND.primary : '#cbd5e1', transition: 'background 0.15s', display: 'flex', justifyContent: taxEnabled ? 'flex-end' : 'flex-start'}}>
                      <span style={{width: 20, height: 20, borderRadius: '50%', background: '#fff', display: 'block'}} />
                    </button>
                  </div>

                  <div style={{marginBottom: 20, opacity: taxEnabled ? 1 : 0.5}}>
                    <label style={labelStyle}>Tax Rate (%)</label>
                    <input type='number' min='0' max='100' step='0.01' value={taxRate}
                      onChange={e => setTaxRate(e.target.value)} disabled={!taxEnabled}
                      style={inputStyle} placeholder='e.g. 20' />
                  </div>

                  <div style={{display: 'flex', justifyContent: 'flex-end'}}>
                    <button type='submit' disabled={taxSaving}
                      style={{display: 'flex', alignItems: 'center', gap: 8, padding: '10px 24px', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, color: '#fff', background: BRAND.primary, cursor: 'pointer', opacity: taxSaving ? 0.7 : 1}}>
                      {taxSaving && <div style={{width: 14, height: 14, border: '2px solid rgba(255,255,255,0.4)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.8s linear infinite'}} />}
                      Save Tax Settings
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {activeTab === 'platform' && (
            <div style={{background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflow: 'hidden', marginTop: 20}}>
              <div style={{padding: '20px 24px', borderBottom: '1px solid #f1f5f9'}}>
                <h3 style={{fontSize: 16, fontWeight: 700, color: '#0f172a', margin: 0}}>Payout Commission</h3>
                <p style={{fontSize: 13, color: '#64748b', margin: '4px 0 0'}}>Platform commission deducted from every organizer and provider payout</p>
              </div>
              <div style={{padding: '24px'}}>
                {commissionMsg && <Alert msg={commissionMsg} />}
                <form onSubmit={handleCommissionSave} style={{maxWidth: 480}}>
                  <div style={{marginBottom: 20}}>
                    <label style={labelStyle}>Commission Rate (%)</label>
                    <input type='number' min='0' max='100' step='0.01' value={commissionRate}
                      onChange={e => setCommissionRate(e.target.value)}
                      style={inputStyle} placeholder='e.g. 15' />
                    <div style={{fontSize: 12, color: '#64748b', marginTop: 6}}>Applies to ticket-sales payouts and service-provider payouts. Default 15%.</div>
                  </div>
                  <div style={{display: 'flex', justifyContent: 'flex-end'}}>
                    <button type='submit' disabled={commissionSaving}
                      style={{display: 'flex', alignItems: 'center', gap: 8, padding: '10px 24px', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, color: '#fff', background: BRAND.primary, cursor: 'pointer', opacity: commissionSaving ? 0.7 : 1}}>
                      {commissionSaving && <div style={{width: 14, height: 14, border: '2px solid rgba(255,255,255,0.4)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.8s linear infinite'}} />}
                      Save Commission
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  )
}

const AccountSettingsWrapper: FC = () => <AccountSettingsPage />

export {AccountSettingsWrapper}
